// =============================
// File: src/pages/admin/ProjectDetail.tsx
// =============================
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { FiEdit2, FiPlay, FiTrash2 } from "react-icons/fi";
import { useAuth } from "../../auth/AuthProvider";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

// API services
import { getLatestRun, queueRun, getById, updateProject, deleteProject, renameProject } from "../../api/projects";
import { getStatus } from "../../api/customers";
import {
    listScenarios,
    listProfiles,
    createScenario as apiCreateScenario,
    deleteScenario as apiDeleteScenario,
    createProfile as apiCreateProfile,
    deleteProfile as apiDeleteProfile,
    updateProfile as apiUpdateProfile,
    queueScenarioRun,
    getComparison,
    Scenario,
    GenerationProfile,
    ScenarioCreatePayload,
    ComparisonData,
} from "../../api/scenarios";

// Custom hooks
import { useModelRunPoller } from "../../hooks/useModelRunPoller";
import { useResourceCheck } from "../../hooks/useResourceCheck";
import { useImpactData } from "../../hooks/useImpactData";

// Utils
import { parseNumericInputs } from "../../utils/formHelpers";

// Types
import {
  ModelInputs,
  ProjectDetail as ProjectDetailType,
} from "../../features/projects/domain/types";
import { ModelResults } from "../../features/model/domain/types";
import { CustomerStatus } from "../../features/customers/domain/types";
import { RunPhase } from "../../features/model/components/RunningIndicator";

// Import shared components
import { RunningIndicator } from "../../features/model/components/RunningIndicator";
import { ResultsViewer } from "../../features/model/components/ResultsViewer";
import { PlanInfernoSection } from "../../features/inferno";
import { RunConfirmationModal } from "../../features/model/components/RunConfirmationModal";
import { ErrorModal } from "../../features/common/components/ErrorModal";
import BaselineComparisonCard from "../../features/baseline/components/BaselineComparisonCard";
import { Modal } from "../../features/common/components/Modal";
import { ModalButtons } from "../../features/common/components/ModalButtons";
import { ProjectDetailForm } from "../../features/projects/components/ProjectDetailForm";
import { ImpactMetrics } from "../../features/model/components/ImpactMetrics";
import { ImpactSummaryCard } from "../../features/model/components/ImpactSummaryCard";
import { ProjectImpactCard } from "../../features/projects/components/ProjectImpactCard";
import { RunModelButton } from "../../features/model/components/RunModelButton";

import { ProjectSummaryCard } from "../../features/model/components/ProjectSummaryCard";

// New Impact & Lifetime Components
import { ImpactExplorer } from "../../features/impact/components/ImpactExplorer";
import { LifetimeProjectionChart } from "../../features/lifetime/components/LifetimeProjectionChart";
import { ImpactModeProvider } from "../../features/impact/context/ImpactModeContext";
import { ImpactModeToggle } from "../../features/impact/components/ImpactModeToggle";

// Report generation
import { GenerateReportButton } from "../../features/report/GenerateReportButton";
import { ExcelDownloadButton } from "../../features/report/components/ExcelDownloadButton";
import { TimeseriesDownloadButton } from "../../features/report/components/TimeseriesDownloadButton";
import { TimeseriesExplorer } from "../../features/report/components/TimeseriesExplorer";
import { GenerationChangeChart } from "../../features/model/components/GenerationChangeChart";
import { GenerationBreakdownTable } from "../../features/model/components/GenerationBreakdownTable";
import { InterconnectionCard } from "../../features/interconnection/components/InterconnectionCard";
import { GeneratorAttributionCard } from "../../features/spatial/components/GeneratorAttributionCard";
import { getLifetimeMultiplier } from "../../features/report/domain/reportDataExtractor";
import { ProfileComparisonData } from "../../features/report/domain/types";

// Scenario & Profile components
import { ScenarioSelector } from "../../features/projects/components/ScenarioSelector";
import { ScenarioCreateModal } from "../../features/projects/components/ScenarioCreateModal";
import { ProfileManager } from "../../features/projects/components/ProfileManager";
import { ScenarioComparisonTable } from "../../features/projects/components/ScenarioComparisonTable";

// Lifetime utilities
import { ImpactTotals } from "../../features/impact/domain/types";
import {
    LifetimeProjection,
    LifetimeProjectionConfig,
    DEFAULT_LIFETIME_CONFIG,
    resolveProjectLifetimeYears,
    generateLifetimeProjection,
} from "../../features/lifetime/domain/types";

export default function ProjectDetail() {
  // Add this ref to the top of your component
  const pageTopRef = useRef<HTMLDivElement>(null);
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const name = decodeURIComponent(projectId || "");
  const client = searchParams.get("client");
  const project_id = searchParams.get("project_id") || ""; // <—— passed from ClientDetail
  const client_id = searchParams.get("client_id");
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const nav = useNavigate();

  // —— State ——
  const [runPhase, setRunPhase] = useState<RunPhase>("idle");
  const [modelResults, setModelResults] = useState<ModelResults | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isEditDetailsOpen, setIsEditDetailsOpen] = useState(false);
  // Add state for the failed run message
  const [failedRunMessage, setFailedRunMessage] = useState<string | null>(null);
  // Track if this is an active polling run vs page load
  const [isActiveRun, setIsActiveRun] = useState(false);
  // Track if the current active run has reached queued/running at least once
  const [hasEnteredActive, setHasEnteredActive] = useState(false);
  // Scenario & profile state
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [showScenarioCreate, setShowScenarioCreate] = useState(false);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);

  // Rename/delete state
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  // —— Custom Hooks ——
  const { data: impactData } = useImpactData(client_id || undefined);

  const { pollForResults, pollingRef } = useModelRunPoller(token, {
    onComplete: (results) => {
      setModelResults(results);
      setRunPhase("idle");
      setIsActiveRun(false);
      setFailedRunMessage(null); // Clear any failed message on success
      setHasEnteredActive(false);
      setComparisonRefreshKey((k) => k + 1);
      // Invalidate parent queries so dashboard/list show fresh data
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["impactData"] });
      queryClient.invalidateQueries({ queryKey: ["partner-clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-dashboard-data"] });
      // Refetch latest run so query cache is in sync (poller already set it, but this ensures consistency)
      queryClient.invalidateQueries({ queryKey: ["projectLatestRun", project_id] });
    },
    onError: (message) => {
      // Ignore transient "failed" reads that refer to the previous run
      // (i.e., before the new run ever reported queued/running)
      if (!hasEnteredActive) {
        return;
      }

      // Only show the modal if this was an active run
      if (isActiveRun) {
        setErrorMessage(message);
        setShowErrorModal(true);
      }

      // Set the persistent failure message for a confirmed active run failure
      setFailedRunMessage("The model run failed to complete. Please press \"Run Model\" to try again.");
      setRunPhase("idle");
      setIsActiveRun(false);
      setHasEnteredActive(false);
    },
    onStatusChange: (status) => {
      setRunPhase(status as RunPhase);
      if (status === "submitting" || status === "running") {
        setHasEnteredActive(true);
      }
    }
  });

  // —— Queries ——
  const {
    data: projectData,
  } = useQuery<ProjectDetailType>({
    queryKey: ["project", project_id],
    enabled: !!project_id && !!token,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    queryFn: async () => {
      if (!project_id || !token) throw new Error("Missing projectId or token");
      return await getById(project_id, token);
    },
  });

  const {
    data: customerStatus,
  } = useQuery<CustomerStatus | null>({
    queryKey: ["customerStatus", client_id],
    enabled: !!client_id && !!token,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    queryFn: async () => {
      if (!client_id || !token) return null;
      return await getStatus(client_id, token);
    },
  });

  const {
    data: latestRun,
    isLoading: isLatestRunLoading,
    refetch: refetchLatestRun,
  } = useQuery<ModelResults | null>({
    queryKey: ["projectLatestRun", project_id, selectedScenarioId],
    enabled: !!project_id && !!token,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    queryFn: async () => {
      try {
        return await getLatestRun(project_id!, token!, selectedScenarioId);
      } catch (error: any) {
        if (error.status === 404) return null; // no prior runs
        throw error;
      }
    },
  });

  // —— Scenario & Profile Queries ——
  const {
    data: scenariosData,
    refetch: refetchScenarios,
  } = useQuery({
    queryKey: ["scenarios", project_id],
    enabled: !!project_id && !!token,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!project_id || !token) return { scenarios: [] as Scenario[] };
      return await listScenarios(project_id, token);
    },
  });

  const scenarios = scenariosData?.scenarios || [];

  // Auto-create a default scenario if none exist, so ProfileManager is always visible
  const [autoCreatingScenario, setAutoCreatingScenario] = useState(false);
  useEffect(() => {
    if (scenarios.length === 0 && project_id && token && scenariosData && !autoCreatingScenario) {
      setAutoCreatingScenario(true);
      apiCreateScenario(project_id, { scenario_name: 'Base Case' }, token)
        .then((created) => {
          setSelectedScenarioId(created.scenario_id);
          refetchScenarios();
        })
        .catch(() => {})
        .finally(() => setAutoCreatingScenario(false));
    }
  }, [scenarios.length, project_id, token, scenariosData, autoCreatingScenario]);

  // Auto-select default scenario
  useEffect(() => {
    if (scenarios.length > 0 && !selectedScenarioId) {
      const defaultScenario = scenarios.find((s: Scenario) => s.is_default);
      setSelectedScenarioId(defaultScenario?.scenario_id || scenarios[0].scenario_id);
    }
  }, [scenarios, selectedScenarioId]);

  // Auto-load comparison data for selected scenario (also re-fetches when runs finish)
  // If the auto-selected scenario has no profiles, fall back to the first scenario that does.
  const [comparisonRefreshKey, setComparisonRefreshKey] = useState(0);
  const [triedScenarioIds, setTriedScenarioIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!project_id || !token || !selectedScenarioId) return;
    getComparison(project_id, token, { scenario_id: selectedScenarioId })
      .then((data) => {
        if (data.profiles.length > 1) {
          setComparisonData(data);
          setTriedScenarioIds(new Set());
        } else {
          setComparisonData(null);
          // If this scenario has no comparison data and there are other scenarios, try the next one
          const nextScenario = scenarios.find(
            (s: Scenario) => s.scenario_id !== selectedScenarioId && !triedScenarioIds.has(s.scenario_id)
          );
          if (nextScenario) {
            setTriedScenarioIds(prev => new Set(prev).add(selectedScenarioId));
            setSelectedScenarioId(nextScenario.scenario_id);
          }
        }
      })
      .catch(() => { /* no comparison data yet */ });
  }, [project_id, token, selectedScenarioId, comparisonRefreshKey]);

  const {
    data: profilesData,
    refetch: refetchProfiles,
  } = useQuery({
    queryKey: ["profiles", project_id],
    enabled: !!project_id && !!token,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!project_id || !token) return { profiles: [] as GenerationProfile[] };
      return await listProfiles(project_id, token);
    },
  });

  const profiles = profilesData?.profiles || [];

  // —— Resource check ——
  const { hasResourcesForRun, resourceLimitMessage } = useResourceCheck(customerStatus || null);

  // —— Form State ——
  const [details, setDetails] = useState<ModelInputs>({
    customer_id: client_id || "",
    project_name: name,
    latitude: 0,
    longitude: 0,
    state: "",
    county: "",
    city: "",
    zip_code: "",
    timezone: "",
    project_nerc_code: "",
    eia_ba_code: "",
    cod_date: "",
    decommision_date: "",
    nameplate_capacity: 100.0,
    poi_limit: 0,
    project_additional_load: 0,
    existing_project_flag: false,
    tech_type_id: "",
    ppa_flag: false,
    ppa_percentage: 0,
    ppa_capacity: 0,
    battery_flag: false,
    battery_capacity_mwh: 0,
    battery_charge_rate_mw: 0,
    battery_discharge_rate_mw: 0,
    battery_round_trip_eff: 0,
    battery_soc_min: 0,
    battery_chemistry: "lfp",
    battery_calendar_life_years: 20.0,
    battery_cycle_life: 6000,
    battery_augmentation_flag: false,
    battery_depth_of_discharge: 0.85,
    project_lifetime_years: "",
    gen_hour_profile_mw: [],
  });

  const [editDetails, setEditDetails] = useState<ModelInputs>(details);

  // —— Effects ——
  // Update details when project data is loaded
  useEffect(() => {
    if (projectData) {
      setDetails(prev => ({ ...prev, ...projectData }));
    }
  }, [projectData]);

  // Update the project_name whenever the URL parameter changes
  useEffect(() => {
    setDetails(prev => ({ ...prev, project_name: name }));
  }, [name]);

  // Hydrate local UI from cached/latest run
  useEffect(() => {
    if (!latestRun) {
      // Don't clear results while polling — the poller will handle completion
      if (!pollingRef.current) {
        setModelResults(null);
      }
      return;
    }

    if (latestRun.status === "completed") {
      setModelResults(latestRun);
      setRunPhase("idle");
      setFailedRunMessage(null); // Clear any failed run message
    } else if (latestRun.status === "queued" || latestRun.status === "running") {
      // Only start polling if not already polling (prevents duplicate loops)
      if (!pollingRef.current) {
        setRunPhase("running");
        setIsActiveRun(true);
        if (project_id) pollForResults(project_id, selectedScenarioId);
      }
    } else if (latestRun.status === "failed") {
      // Do not re-assert the message if we're actively trying to start a run
      if (!isActiveRun && !pollingRef.current) {
        setFailedRunMessage("The most recent run for this project failed. Please press \"Run Model\" to try again.");
        setRunPhase("idle");
      }
    }
  }, [latestRun, project_id, selectedScenarioId, pollForResults, isActiveRun, pollingRef]);

  // Add this effect to scroll to top on mount and when project changes
  useEffect(() => {
    // Scroll to top of page
    window.scrollTo(0, 0);
    // Alternative: scroll to the top ref element
    pageTopRef.current?.scrollIntoView({ behavior: "auto" });
  }, [projectId]); // Re-run when project ID changes

  // —— Derived Data ——
  const isDetailsComplete = useMemo(() => !!project_id, [project_id]);

  const projectImpact = useMemo(() => {
    if (project_id && impactData?.projectImpacts) {
      return impactData.projectImpacts[project_id];
    }
    return null;
  }, [project_id, impactData]);

  // Check if this is a future project (NOT an existing project)
  // Must explicitly check for existing_project_flag === true to avoid false positives
  const isFutureProject = useMemo(() => {
    // If any source says it's an existing project, it's NOT a future project
    if (projectData?.existing_project_flag === true) return false;
    if (modelResults?.run_type === 'existing') return false;
    if (details.existing_project_flag === true) return false;
    // Only return true if we have confirmed it's a future project
    if (projectData?.existing_project_flag === false) return true;
    if (modelResults?.run_type === 'future') return true;
    // Default to false (don't show lifetime projection) until we have data
    return false;
  }, [projectData?.existing_project_flag, details.existing_project_flag, modelResults?.run_type]);

  // Backend emits Cradle_to_Grave_Analysis (full cradle-to-grave totals) and
  // Operational_Analysis (ops-only, cradle-to-grave minus infrastructure).
  // impactTotals here holds the full cradle-to-grave totals.
  const impactTotals: ImpactTotals | null = useMemo(() => {
    if (!modelResults?.summary_json?.Impacts?.Cradle_to_Grave_Analysis?.Total) return null;
    const total = modelResults.summary_json.Impacts.Cradle_to_Grave_Analysis.Total;
    const summaryJson = modelResults.summary_json;

    // Get generation from generation_metadata (most reliable source)
    let generationMwh = 0;
    if (summaryJson.generation_metadata?.actual_generation_gwh) {
      generationMwh = summaryJson.generation_metadata.actual_generation_gwh * 1000;
    } else if (summaryJson.benefit_frame?.renewable_contribution?.total_gwh) {
      generationMwh = summaryJson.benefit_frame.renewable_contribution.total_gwh * 1000;
    } else {
      // Fallback: estimate from capacity and typical capacity factor
      const capacity = typeof details.nameplate_capacity === 'number'
        ? details.nameplate_capacity
        : parseFloat(String(details.nameplate_capacity)) || 0;
      generationMwh = capacity * 8760 * 0.25;
    }

    // Extract avoided CO2e from benefit_frame or emissions (in tonnes, convert to kg)
    let co2eKg = 0;
    if (summaryJson.benefit_frame?.emissions_avoided?.Carbon) {
      co2eKg = summaryJson.benefit_frame.emissions_avoided.Carbon * 1000;
    } else if (summaryJson.Emissions?.Cradle_to_Grave_Analysis?.air) {
      const carbonEmission = summaryJson.Emissions.Cradle_to_Grave_Analysis.air.find(
        (e: { Name: string; Value: number }) => e.Name === 'Carbon'
      );
      if (carbonEmission) {
        co2eKg = carbonEmission.Value * 1000;
      }
    }

    return {
      dalys: total.DALYs || 0,
      pdfs: total.PDFs || 0,
      usd: total.USD || 0,
      co2eKg,
      generationMwh,
    };
  }, [modelResults, details.nameplate_capacity]);

  // Ops totals — from Operational_Analysis (ops-only, cradle-to-grave minus infrastructure)
  const opsTotals: ImpactTotals | null = useMemo(() => {
    if (!modelResults?.summary_json?.Impacts?.Operational_Analysis?.Total) return null;
    const opsTotal = modelResults.summary_json.Impacts.Operational_Analysis.Total;
    return {
      dalys: opsTotal.DALYs || 0,
      pdfs: opsTotal.PDFs || 0,
      usd: opsTotal.USD || 0,
      co2eKg: impactTotals?.co2eKg || 0,
      generationMwh: impactTotals?.generationMwh || 0,
    };
  }, [modelResults, impactTotals]);

  // Raw totals objects for ImpactModeProvider
  const netTotalsRaw = useMemo(() => {
    if (!modelResults?.summary_json?.Impacts?.Cradle_to_Grave_Analysis?.Total) return null;
    return modelResults.summary_json.Impacts.Cradle_to_Grave_Analysis.Total;
  }, [modelResults]);

  const opsTotalsRaw = useMemo(() => {
    if (!modelResults?.summary_json?.Impacts?.Operational_Analysis?.Total) return null;
    return modelResults.summary_json.Impacts.Operational_Analysis.Total;
  }, [modelResults]);

  // Generate lifetime projection (for FUTURE projects only - existing projects show actual data)
  const lifetimeProjection: LifetimeProjection | null = useMemo(() => {
    // Only generate lifetime projection for future projects
    if (!isFutureProject || !modelResults || !impactTotals) return null;

    const techType = details.tech_type_id || 'solar_ground';
    const techConfig = DEFAULT_LIFETIME_CONFIG[techType] || DEFAULT_LIFETIME_CONFIG.solar_ground;

    const config: LifetimeProjectionConfig = {
      // The project's stored lifetime, else the run's stamped one, else the default.
      projectLifetimeYears: resolveProjectLifetimeYears(
        details.project_lifetime_years,
        (modelResults.summary_json as any)?.execution_metadata?.project_lifetime?.years,
      ),
      socialDiscountRate: 0.03,
      annualDegradationPct: techConfig.annualDegradationPct || 0.005,
      degradationModel: techConfig.degradationModel || 'exponential',
      includeConstructionImpacts: false,
      includeDecommissioningImpacts: false,
    };

    const baseYearMwh = impactTotals.generationMwh;
    if (baseYearMwh <= 0) return null;

    const impactRates = {
      dalysPerMwh: impactTotals.dalys / baseYearMwh,
      pdfsPerMwh: impactTotals.pdfs / baseYearMwh,
      usdPerMwh: impactTotals.usd / baseYearMwh,
      co2eKgPerMwh: impactTotals.co2eKg / baseYearMwh,
    };

    const codYear = details.cod_date
      ? new Date(details.cod_date).getFullYear()
      : new Date().getFullYear() + 1;

    const yearlyProjections = generateLifetimeProjection(baseYearMwh, impactRates, config, codYear);

    const lifetimeGenerationGwh = yearlyProjections.reduce((sum, yr) => sum + yr.projectedGenerationMwh, 0) / 1000;
    const lifetimeAvoidedCo2eTonnes = yearlyProjections.reduce((sum, yr) => sum + yr.avoidedCo2eKg, 0) / 1000;
    const lifetimeAvoidedDalys = yearlyProjections.reduce((sum, yr) => sum + yr.avoidedDalys, 0);
    const lifetimeAvoidedPdfs = yearlyProjections.reduce((sum, yr) => sum + yr.avoidedPdfs, 0);
    const lifetimeEconomicNominalUsd = yearlyProjections.reduce((sum, yr) => sum + yr.economicValueNominalUsd, 0);
    const lifetimeEconomicNpvUsd = yearlyProjections.reduce((sum, yr) => sum + yr.economicValueNpvUsd, 0);

    const capacity = typeof details.nameplate_capacity === 'number'
      ? details.nameplate_capacity
      : parseFloat(String(details.nameplate_capacity)) || 1;

    return {
      projectId: project_id || '',
      config,
      codYear,
      baseYearGenerationMwh: baseYearMwh,
      baseYearCapacityFactor: baseYearMwh / (capacity * 8760),
      impactRates,
      yearlyProjections,
      lifetimeGenerationGwh,
      lifetimeAvoidedCo2eTonnes,
      lifetimeAvoidedDalys,
      lifetimeAvoidedPdfs,
      lifetimeEconomicNominalUsd,
      lifetimeEconomicNpvUsd,
      infrastructureCo2eTonnes: null,
      netLifetimeCo2eTonnes: lifetimeAvoidedCo2eTonnes,
      calculatedAt: new Date().toISOString(),
      modelVersion: '1.0',
    };
  }, [isFutureProject, modelResults, impactTotals, details, project_id]);

  // Transform comparison data for report (ProfileComparisonData format)
  const profileComparisonForReport: ProfileComparisonData | undefined = useMemo(() => {
    if (!comparisonData || comparisonData.profiles.length < 2) return undefined;
    return {
      profiles: comparisonData.profiles
        .filter(p => p.status === "completed")
        .map(p => ({
          label: p.profile_label,
          generationMwh: p.generation_mwh || 0,
          dalys: p.dalys || 0,
          pdfs: p.pdfs || 0,
          usd: p.usd || 0,
          co2eKg: p.co2e_kg || 0,
          healthBreakdown: p.health_breakdown?.map(e => ({ category: e.category, value: e.value || 0, usd: e.usd || 0 })),
          biodiversityBreakdown: p.biodiversity_breakdown?.map(e => ({ category: e.category, value: e.value || 0, usd: e.usd || 0 })),
          economicBreakdown: p.economic_breakdown?.map(e => ({ category: e.category, value: e.value || 0, usd: e.usd || 0 })),
        })),
    };
  }, [comparisonData]);

  // —— Handlers ——
  const handleRunModel = () => {
    if (!isDetailsComplete || !project_id) return;

    // First clear the message
    setFailedRunMessage(null);

    // Then show the confirmation modal in the next render cycle
    requestAnimationFrame(() => {
      setShowConfirmModal(true);
    });
  };

  const runModelNow = async () => {
    setShowConfirmModal(false);
    setModelResults(null);
    setRunPhase("submitting");
    setFailedRunMessage(null); // Clear any failed run message
    setIsActiveRun(true); // Mark this as an active run
    setHasEnteredActive(false);

    try {
      await queueRun(project_id!, token!);

      queryClient.setQueryData<ModelResults | null>(["projectLatestRun", project_id, selectedScenarioId ?? null], {
        status: "queued",
        summary_json: {},
      } as ModelResults);

      setRunPhase("running");
      pollForResults(project_id!, selectedScenarioId);
    } catch (err: any) {
      setErrorMessage(err.message ?? String(err));
      setShowErrorModal(true);
      setRunPhase("idle");
      setIsActiveRun(false);
      setHasEnteredActive(false);
      setFailedRunMessage("Failed to start the model run. Please try again.");
    }
  };

  // —— Scenario Handlers ——
  const handleCreateScenario = async (payload: ScenarioCreatePayload) => {
    if (!project_id || !token) return;
    try {
      const created = await apiCreateScenario(project_id, payload, token);
      setShowScenarioCreate(false);
      setSelectedScenarioId(created.scenario_id);
      refetchScenarios();
    } catch (err: any) {
      setErrorMessage(err.message ?? String(err));
      setShowErrorModal(true);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    if (!project_id || !token) return;
    if (!window.confirm("Delete this scenario and all its runs?")) return;
    try {
      await apiDeleteScenario(project_id, scenarioId, token);
      if (selectedScenarioId === scenarioId) {
        const defaultS = scenarios.find((s: Scenario) => s.is_default);
        setSelectedScenarioId(defaultS?.scenario_id || null);
      }
      refetchScenarios();
    } catch (err: any) {
      setErrorMessage(err.message ?? String(err));
      setShowErrorModal(true);
    }
  };

  // —— Profile Handlers ——
  const handleUploadProfile = async (
    label: string,
    confidenceLevel: number | null,
    data: Array<{ timestamp: string; value: number }>,
    isPrimary: boolean,
  ) => {
    if (!project_id || !token) return;
    try {
      await apiCreateProfile(project_id, {
        profile_label: label,
        confidence_level: confidenceLevel ?? undefined,
        scenario_id: selectedScenarioId ?? undefined,
        gen_hour_profile_mw: data,
        is_primary: isPrimary,
      }, token);
      refetchProfiles();
    } catch (err: any) {
      setErrorMessage(err.message ?? String(err));
      setShowErrorModal(true);
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!project_id || !token) return;
    try {
      await apiDeleteProfile(project_id, profileId, token);
      refetchProfiles();
    } catch (err: any) {
      setErrorMessage(err.message ?? String(err));
      setShowErrorModal(true);
    }
  };

  const handleSetPrimaryProfile = async (profileId: string) => {
    if (!project_id || !token) return;
    try {
      await apiUpdateProfile(project_id, profileId, { is_primary: true }, token);
      refetchProfiles();
    } catch (err: any) {
      setErrorMessage(err.message ?? String(err));
      setShowErrorModal(true);
    }
  };

  // —— Run All Profiles ——
  const handleRunAllProfiles = async () => {
    if (!project_id || !token || !selectedScenarioId) return;
    // Don't clear modelResults — keep existing P50 results visible during multi-profile run
    setRunPhase("submitting");
    setFailedRunMessage(null);
    setIsActiveRun(true);
    setHasEnteredActive(false);

    try {
      const result = await queueScenarioRun(project_id, token, {
        scenario_id: selectedScenarioId,
        run_all_profiles: true,
      });

      if ("run_group_id" in result) {
        // Multi-profile run — start polling comparison
        setRunPhase("running");
        const pollComparison = async () => {
          const data = await getComparison(project_id, token, {
            run_group_id: result.run_group_id,
          });
          setComparisonData(data);
          const allDone = data.profiles.every(
            (p) => p.status === "completed" || p.status === "failed",
          );
          if (allDone) {
            setRunPhase("idle");
            setIsActiveRun(false);
            refetchScenarios();
            refetchLatestRun();
            setComparisonRefreshKey((k) => k + 1);
          } else {
            setTimeout(pollComparison, 5000);
          }
        };
        setTimeout(pollComparison, 3000);
      } else {
        // Single run
        setRunPhase("running");
        pollForResults(project_id);
      }
    } catch (err: any) {
      setErrorMessage(err.message ?? String(err));
      setShowErrorModal(true);
      setRunPhase("idle");
      setIsActiveRun(false);
    }
  };

  // —— Details handlers ——
  const [isSavingDetails, setIsSavingDetails] = useState(false);

  const openDetailsModal = () => {
    setEditDetails(details);
    setIsEditDetailsOpen(true);
  };

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project_id || !token) return;

    setIsSavingDetails(true);
    try {
      const parsedDetails = parseNumericInputs(editDetails);
      await updateProject(project_id, parsedDetails, token);
      setDetails(parsedDetails);
      setIsEditDetailsOpen(false);
      // Invalidate the project query to refresh data
      queryClient.invalidateQueries({ queryKey: ["project", project_id] });
    } catch (err: any) {
      setErrorMessage(err.message ?? "Failed to save project details");
      setShowErrorModal(true);
    } finally {
      setIsSavingDetails(false);
    }
  };

  // Rename project mutation
  const renameMutation = useMutation({
    mutationFn: async (newName: string) => {
      if (!project_id || !token) throw new Error("Missing data");
      return await renameProject(project_id, newName, token);
    },
    onSuccess: (data) => {
      setIsRenameOpen(false);
      queryClient.invalidateQueries({ queryKey: ["project", project_id] });
      queryClient.invalidateQueries({ queryKey: ["projects", client_id] });
      // Navigate to updated URL with new name
      const params = new URLSearchParams();
      if (client) params.set("client", client);
      if (project_id) params.set("project_id", project_id);
      if (client_id) params.set("client_id", client_id);
      nav(`/admin/projects/${encodeURIComponent(data.new_name)}?${params.toString()}`, { replace: true });
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || "Failed to rename project");
      setShowErrorModal(true);
    },
  });

  // Canonical project name from API data (most reliable), fallback to URL
  const canonicalName = projectData?.project_name ?? name;

  // Delete project mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!project_id || !token) throw new Error("Missing data");
      return await deleteProject(project_id, canonicalName, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", client_id] });
      setIsDeleteOpen(false);
      nav(client_id ? `/admin/clients/${client_id}` : "/admin/clients");
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || "Failed to delete project");
      setShowErrorModal(true);
    },
  });

  /* ——— RENDER ——— */
  return (
    <div className="min-h-screen overflow-y-auto pt-0 pb-8">
      {/* Add ref to the top of your component */}
      <div ref={pageTopRef}></div>

      <ImpactModeProvider netTotals={netTotalsRaw ?? null} opsTotals={opsTotalsRaw ?? null}>
      {/* Unified Project Summary Card */}
      <ProjectSummaryCard
        projectName={name}
        techType={details.tech_type_id || 'solar_ground'}
        capacity={typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
        location={`${details.latitude}, ${details.longitude}`}
        analysisYear={modelResults?.year || new Date().getFullYear()}
        impactTotals={impactTotals}
        opsTotals={opsTotals}
        lifetimeProjection={lifetimeProjection}
        onEditDetails={openDetailsModal}
        onRunModel={handleRunModel}
        onRename={() => { setRenameValue(canonicalName); setIsRenameOpen(true); }}
        onDelete={() => { setDeleteConfirm(""); setIsDeleteOpen(true); }}
        canRun={isDetailsComplete && runPhase === "idle" && hasResourcesForRun}
        lensToggle={modelResults ? <ImpactModeToggle /> : undefined}
        baCode={details.eia_ba_code || undefined}
        generationMwh={impactTotals?.generationMwh}
        isExisting={projectData?.existing_project_flag}
        generationData={modelResults?.summary_json?.Generation}
        avoidedCo2eTonnes={impactTotals?.co2eKg ? impactTotals.co2eKg / 1000 : undefined}
        backTo={{ to: client_id ? `/admin/clients/${client_id}` : "/admin/clients", label: client ?? "Clients" }}
        reportButtons={modelResults && impactTotals ? (
          <>
            <TimeseriesDownloadButton
              projectId={project_id!}
              projectName={name}
              token={token}
            />
            <TimeseriesExplorer
              projectId={project_id!}
              projectName={name}
              token={token}
            />
            <ExcelDownloadButton
              projectName={name}
              techType={details.tech_type_id || 'solar_ground'}
              capacityMw={typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
              location={`${details.latitude}, ${details.longitude}`}
              analysisYear={modelResults.year || new Date().getFullYear()}
              isFutureProject={isFutureProject}
              codDate={typeof details.cod_date === 'string' ? details.cod_date : undefined}
              customerName={client || undefined}
              county={typeof details.county === 'string' ? details.county : undefined}
              existingProjectFlag={!isFutureProject}
              modelResults={modelResults}
              impactTotals={impactTotals}
              lifetimeProjection={lifetimeProjection}
              latitude={typeof details.latitude === 'number' ? details.latitude : parseFloat(String(details.latitude)) || undefined}
              longitude={typeof details.longitude === 'number' ? details.longitude : parseFloat(String(details.longitude)) || undefined}
              eiaBaCode={typeof details.eia_ba_code === 'string' ? details.eia_ba_code : undefined}
              nercCode={typeof details.project_nerc_code === 'string' ? details.project_nerc_code : undefined}
              state={typeof details.state === 'string' ? details.state : undefined}
              profileComparison={profileComparisonForReport}
            />
            <GenerateReportButton
              projectName={name}
              techType={details.tech_type_id || 'solar_ground'}
              capacityMw={typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
              location={`${details.latitude}, ${details.longitude}`}
              analysisYear={modelResults.year || new Date().getFullYear()}
              isFutureProject={isFutureProject}
              codDate={typeof details.cod_date === 'string' ? details.cod_date : undefined}
              customerName={client || undefined}
              county={typeof details.county === 'string' ? details.county : undefined}
              existingProjectFlag={!isFutureProject}
              modelResults={modelResults}
              impactTotals={impactTotals}
              lifetimeProjection={lifetimeProjection}
              latitude={typeof details.latitude === 'number' ? details.latitude : parseFloat(String(details.latitude)) || undefined}
              longitude={typeof details.longitude === 'number' ? details.longitude : parseFloat(String(details.longitude)) || undefined}
              eiaBaCode={typeof details.eia_ba_code === 'string' ? details.eia_ba_code : undefined}
              nercCode={typeof details.project_nerc_code === 'string' ? details.project_nerc_code : undefined}
              state={typeof details.state === 'string' ? details.state : undefined}
              profileComparison={profileComparisonForReport}
            />
          </>
        ) : undefined}
      />

      {/* Profiles & Scenarios — collapsible */}
      {scenarios.length > 0 && (
        <details className="mt-4 rounded-lg bg-surface-700 border border-border">
          <summary className="px-4 py-3 cursor-pointer text-body-sm font-medium text-text-secondary hover:text-text-primary transition-colors select-none">
            Profiles & Scenarios
            {profiles.length > 0 && <span className="ml-2 text-text-muted">({profiles.length} profile{profiles.length !== 1 ? 's' : ''})</span>}
          </summary>
          <div className="px-4 pb-4 space-y-3 border-t border-border/50 pt-3">
            <div className="flex items-center gap-3 flex-wrap">
              <ScenarioSelector
                scenarios={scenarios}
                selectedScenarioId={selectedScenarioId}
                onSelect={setSelectedScenarioId}
                onCreate={() => setShowScenarioCreate(true)}
                onClone={(sid) => {
                  const s = scenarios.find((sc: Scenario) => sc.scenario_id === sid);
                  if (s) handleCreateScenario({
                    scenario_name: `${s.scenario_name} (copy)`,
                    clone_from_scenario_id: sid,
                  });
                }}
                onDelete={handleDeleteScenario}
              />
              {profiles.length >= 1 && (
                <button
                  onClick={handleRunAllProfiles}
                  disabled={runPhase !== "idle"}
                  className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-quantum-400 border-quantum-500/40 bg-quantum-500/10 hover:bg-quantum-500/20"
                >
                  {profiles.length > 1 ? `Run All Profiles (${profiles.length})` : "Run Scenario"}
                </button>
              )}
            </div>
            <ProfileManager
              profiles={profiles}
              onUpload={handleUploadProfile}
              onDelete={handleDeleteProfile}
              onSetPrimary={handleSetPrimaryProfile}
              samProfileFlag={projectData?.sam_profile_flag}
              generationMetadata={modelResults?.summary_json?.generation_metadata}
            />
          </div>
        </details>
      )}

      {/* Scenario Comparison Table */}
      {comparisonData && comparisonData.profiles.length > 1 && (
        <div className="mt-4">
          <ScenarioComparisonTable
            profiles={comparisonData.profiles}
            scenarioName={scenarios.find((s: Scenario) => s.scenario_id === selectedScenarioId)?.scenario_name}
          />
        </div>
      )}

      {/* Report buttons are now in the ProjectSummaryCard header */}

      {/* Failed run message */}
      {failedRunMessage && (
        <div className="mt-4 rounded-lg bg-health-500/10 p-4 border border-health-500/30">
          <p className="text-sm text-health-400">{failedRunMessage}</p>
        </div>
      )}

      {/* Empty state — no previous runs */}
      {!modelResults && !isLatestRunLoading && runPhase === "idle" && !failedRunMessage && (
        <div className="mt-4 rounded-lg bg-surface-700 p-5 border border-border">
          <p className="text-sm text-text-secondary mb-3">
            <span className="font-medium text-text-primary">No model runs yet.</span> Click Run above to analyze this project.
          </p>
        </div>
      )}

      {/* Indicators and Results */}
      <RunningIndicator phase={runPhase} />

      {/* Impact Metrics - only show when results are available */}
      {modelResults && (
        <ImpactMetrics
          modelResults={modelResults}
          projectName={name}
          lifetimeProjection={lifetimeProjection}
        />
      )}

      {/* Grid Baseline Comparison — show after impact metrics */}
      {modelResults && impactTotals && impactTotals.generationMwh > 0 && (
        <BaselineComparisonCard
          baCode={typeof details.eia_ba_code === 'string' ? details.eia_ba_code : null}
          projectDalysPerMwh={impactTotals.dalys / impactTotals.generationMwh}
          projectPdfsPerMwh={impactTotals.pdfs / impactTotals.generationMwh}
          projectUsdPerMwh={impactTotals.usd / impactTotals.generationMwh}
          projectGenerationMwh={impactTotals.generationMwh}
        />
      )}

      {/* Lifetime Projection Chart — future projects only */}
      {lifetimeProjection && (
        <LifetimeProjectionChart projection={lifetimeProjection} />
      )}

      {/* Generation Displacement Chart */}
      {modelResults?.summary_json?.Generation && (
        <GenerationChangeChart generationData={modelResults.summary_json.Generation} profileLabel={modelResults.profile_label} />
      )}

      {/* Per-fuel generation table (Historical / Difference / Interconnection) */}
      {modelResults?.summary_json?.Generation && (
        <GenerationBreakdownTable
          generationData={modelResults.summary_json.Generation}
          interconnectionBreakdown={modelResults.summary_json.interconnection_breakdown}
          baCode={typeof details.eia_ba_code === "string" ? details.eia_ba_code : undefined}
          profileLabel={modelResults.profile_label}
        />
      )}

      {/* Cross-Border Impact — interconnection displacement visualization */}
      {modelResults?.summary_json?.interconnection_breakdown &&
       modelResults.summary_json.interconnection_breakdown.neighbors &&
       modelResults.summary_json.interconnection_breakdown.neighbors.length > 0 && (
        <InterconnectionCard
          breakdown={modelResults.summary_json.interconnection_breakdown as any}
          totalGenerationGwh={impactTotals?.generationMwh ? impactTotals.generationMwh / 1000 : 0}
        />
      )}

      {/* Generator Attribution — where your impact lands */}
      {modelResults?.summary_json?.generator_attribution &&
       modelResults.summary_json.generator_attribution.n_generators > 0 && (
        <GeneratorAttributionCard
          attribution={modelResults.summary_json.generator_attribution}
          congestion={modelResults.summary_json.congestion_metadata ?? undefined}
          projectLatitude={typeof projectData?.latitude === 'number' ? projectData.latitude : undefined}
          projectLongitude={typeof projectData?.longitude === 'number' ? projectData.longitude : undefined}
          projectId={project_id}
          projectName={canonicalName}
        />
      )}

      {/* Impact Explorer — all projects with model results or comparison data */}
      {(modelResults || (comparisonData && comparisonData.profiles.length > 1)) && project_id && (
        <div className="mt-6">
          <ImpactExplorer
            projectId={project_id}
            isFutureProject={isFutureProject}
            comparisonProfiles={comparisonData && comparisonData.profiles.length > 1 ? comparisonData.profiles : undefined}
          />
        </div>
      )}

      {/* Show impact data from the hook if available and no model results */}
      {!modelResults && !isLatestRunLoading && projectImpact && (
        <ProjectImpactCard impact={projectImpact} />
      )}

      {modelResults && (
        <ResultsViewer
          modelResults={modelResults}
          projectName={name}
          lifetimeMultiplier={getLifetimeMultiplier(lifetimeProjection)}
          lifetimeYears={lifetimeProjection?.config.projectLifetimeYears}
        />
      )}

      {/* Plan Inferno — feature-flagged via REACT_APP_ENABLE_INFERNO. */}
      {modelResults?.id && project_id && (
        <PlanInfernoSection projectId={project_id} runId={modelResults.id} />
      )}
      </ImpactModeProvider>

      {/* Modals */}
      <RunConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={runModelNow}
        customerStatus={customerStatus || null}
        projectCapacity={details.nameplate_capacity}
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorMessage}
      />

      {/* Edit Details Modal */}
      {isEditDetailsOpen && (
        <Modal title="Edit Project Details" onClose={() => !isSavingDetails && setIsEditDetailsOpen(false)}>
          <form onSubmit={handleDetailsSubmit} className="flex flex-col h-[75vh]">
            <ProjectDetailForm
              details={editDetails}
              onChange={(name, value) => setEditDetails(prev => ({ ...prev, [name]: value }))}
            />
            <ModalButtons
              onCancel={() => setIsEditDetailsOpen(false)}
              disabled={isSavingDetails}
              submitLabel={isSavingDetails ? "Saving..." : "Save Changes"}
            />
          </form>
        </Modal>
      )}

      {/* Rename Project Modal */}
      {isRenameOpen && (
        <Modal title="Rename Project" onClose={() => !renameMutation.isPending && setIsRenameOpen(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (renameValue.trim() && renameValue.trim() !== canonicalName) {
                renameMutation.mutate(renameValue.trim());
              }
            }}
            className="space-y-4"
          >
            <div>
              <label className="input-label">Project Name</label>
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="input"
                autoFocus
              />
            </div>
            <ModalButtons
              onCancel={() => setIsRenameOpen(false)}
              disabled={renameMutation.isPending || !renameValue.trim() || renameValue.trim() === canonicalName}
              submitLabel={renameMutation.isPending ? "Renaming..." : "Rename"}
            />
          </form>
        </Modal>
      )}

      {/* Delete Project Confirmation Modal */}
      {isDeleteOpen && (
        <Modal title="Delete Project" onClose={() => !deleteMutation.isPending && setIsDeleteOpen(false)}>
          <div className="space-y-4">
            <div className="rounded-lg bg-health-500/10 border border-health-500/30 p-4 text-body-sm text-health-400">
              <p className="font-medium">This action cannot be undone.</p>
              <p className="mt-2">
                This will permanently delete <strong>{canonicalName}</strong> and all associated model runs.
              </p>
            </div>

            <div>
              <label className="input-label">
                Type <strong>{canonicalName}</strong> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                className="input"
                placeholder={canonicalName}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteOpen(false)}
                className="btn-secondary"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteConfirm.trim() !== canonicalName || deleteMutation.isPending}
                className="rounded-lg bg-health-500 px-4 py-2 text-body-sm font-medium text-white hover:bg-health-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete Project"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Scenario Create Modal */}
      <ScenarioCreateModal
        isOpen={showScenarioCreate}
        onClose={() => setShowScenarioCreate(false)}
        onSubmit={handleCreateScenario}
        existingScenarios={scenarios}
      />
    </div>
  );
}